/**
 * Suaviza os contornos que chegam em degraus de pixel (o rastreio das nuvens
 * vem numa grade de 0,045°, ~5 km; o da chuva, na de 0,1° do GSMaP): cada
 * borda era uma escada, e o traço parecia grosseiro (pedido do usuário,
 * 03/11/2026: "a linha ainda está grosseira", "quero algo mais profissional").
 *
 * Só o DESENHO muda: a geometria da feição continua a original (as cidades no
 * caminho, a posição da ficha, o cone e o toque usam ela), e a curva fica à
 * parte, na propriedade `desenho`, que o estilo usa (`geometriaDoDesenho`).
 * Área, temperatura e o resto da ficha vêm do servidor. Corte de cantos de Chaikin (cada aresta
 * vira os pontos a 1/4 e 3/4 dela) depois de tirar os vértices colineares:
 * a escada vira curva, e o contorno fica dentro do original, a menos de meio
 * degrau dele.
 */
import type Feature from 'ol/Feature';
import type { FeatureLike } from 'ol/Feature';
import type Geometry from 'ol/geom/Geometry';
import MultiPolygon from 'ol/geom/MultiPolygon';
import Polygon from 'ol/geom/Polygon';

type Ponto = number[];

/** Passadas do Chaikin: com 3, a escada de 5 km some na tela e o anel cresce ~8× nos cantos que restam. */
export const PASSADAS = 3;

/** Tira os vértices no meio de um trecho reto (a escada tem muitos). O anel vem fechado (último = primeiro). */
function semColineares(anel: readonly Ponto[]): Ponto[] {
  const n = anel.length - 1;
  if (n < 4) return anel.slice(0, n);
  const fora: Ponto[] = [];
  for (let i = 0; i < n; i++) {
    const a = anel[(i - 1 + n) % n]!;
    const b = anel[i]!;
    const c = anel[(i + 1) % n]!;
    const cruz = (b[0]! - a[0]!) * (c[1]! - b[1]!) - (b[1]! - a[1]!) * (c[0]! - b[0]!);
    const escala = Math.hypot(b[0]! - a[0]!, b[1]! - a[1]!) * Math.hypot(c[0]! - b[0]!, c[1]! - b[1]!);
    if (escala === 0 || Math.abs(cruz) > 1e-9 * escala) fora.push(b);
  }
  return fora.length >= 3 ? fora : anel.slice(0, n);
}

/** Um anel suavizado (fechado). Anéis pequenos demais ficam como estão. */
export function suavizarAnel(anel: readonly Ponto[], passadas = PASSADAS): Ponto[] {
  if (anel.length < 4) return anel.slice();
  let p = semColineares(anel);
  if (p.length < 3) return anel.slice();
  for (let k = 0; k < passadas; k++) {
    const q: Ponto[] = [];
    for (let i = 0; i < p.length; i++) {
      const a = p[i]!;
      const b = p[(i + 1) % p.length]!;
      q.push([0.75 * a[0]! + 0.25 * b[0]!, 0.75 * a[1]! + 0.25 * b[1]!], [0.25 * a[0]! + 0.75 * b[0]!, 0.25 * a[1]! + 0.75 * b[1]!]);
    }
    p = q;
  }
  p.push(p[0]!.slice());
  return p;
}

/** Guarda na feição o desenho suavizado dos polígonos dela; a geometria fica como veio. */
export function suavizarFeicao(f: Feature): void {
  const g = f.getGeometry();
  if (g instanceof Polygon) f.set('desenho', new Polygon(g.getCoordinates().map((anel) => suavizarAnel(anel))), true);
  else if (g instanceof MultiPolygon) f.set('desenho', new MultiPolygon(g.getCoordinates().map((poli) => poli.map((anel) => suavizarAnel(anel)))), true);
}

/** O que o estilo desenha: a curva, se houver; senão, a geometria. */
export function geometriaDoDesenho(f: FeatureLike): Geometry | undefined {
  return (f.get('desenho') as Geometry | undefined) ?? (f.getGeometry() as Geometry | undefined);
}
